import { Alert, Button, Card, Checkbox, Form, Input, InputNumber, Modal, Select, Space, Table, Tabs, Typography, message } from "antd";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../services/api";
import { DataTable, useTableParams } from "../../components/DataTable";
import { fmtDateTime, fmtNumber, StatusTag, Tags } from "../../components/format";

export function KnowledgePage() {
  const [tab, setTab] = useState("documents");
  return (
    <Tabs
      activeKey={tab}
      onChange={setTab}
      items={[
        { key: "documents", label: "文档", children: <Documents /> },
        { key: "bases", label: "知识库", children: <Bases /> },
      ]}
    />
  );
}

function Bases() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["knowledgeBases"],
    queryFn: () => api.get<Record<string, unknown>[]>("/api/v1/admin/knowledge/bases"),
  });
  if (error) return <Alert type="error" showIcon message={(error as Error).message} />;

  return (
    <Card styles={{ body: { padding: 12 } }}>
      <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
        `trustLevel` 决定内容能不能进入模型上下文以及以什么身份出现。
        外部抓取的内容默认 untrusted，会给 BrainRun 打上 triggeredByUntrustedContent，
        进而禁止执行 guarded Skill、禁止写记忆、禁止改关系值——这是防间接提示注入的核心机制。
      </Typography.Paragraph>
      <Table
        size="small"
        rowKey="id"
        loading={isLoading}
        pagination={false}
        dataSource={data ?? []}
        columns={[
          { title: "key", dataIndex: "key", width: 200 },
          { title: "名称", dataIndex: "displayName", width: 180 },
          { title: "范围", dataIndex: "scope", width: 200 },
          { title: "可信级别", dataIndex: "trustLevel", width: 130, render: (v) => <StatusTag value={v} /> },
          { title: "状态", dataIndex: "status", width: 100, render: (v) => <StatusTag value={v} /> },
          {
            title: "文档",
            dataIndex: "_count",
            width: 90,
            align: "right",
            render: (v: { documents: number }) => fmtNumber(v?.documents),
          },
          {
            title: "切片",
            dataIndex: "_count",
            width: 90,
            align: "right",
            render: (v: { chunks: number }) => fmtNumber(v?.chunks),
          },
        ]}
      />
    </Card>
  );
}

const DOC_FILTERS = ["search", "status"];

function Documents() {
  const { searchParams, setParam } = useTableParams(DOC_FILTERS);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const [rebuildOpen, setRebuildOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [createForm] = Form.useForm();
  const [rebuildForm] = Form.useForm();
  const { data: bases = [] } = useQuery({
    queryKey: ["knowledgeBases"],
    queryFn: () => api.get<Array<{ id: string; key: string; displayName: string; status: string }>>("/api/v1/admin/knowledge/bases"),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["/api/v1/admin/knowledge/documents"] });

  const createDocument = async () => {
    const values = await createForm.validateFields();
    setSaving(true);
    try {
      const result = await api.post<{ id: string }>("/api/v1/admin/knowledge/documents", {
        ...values,
        chunkingParams: {
          targetChars: values.targetChars,
          overlapChars: values.overlapChars,
          splitBy: values.splitBy,
        },
      });
      message.success("知识草稿已创建");
      setCreateOpen(false);
      createForm.resetFields();
      await refresh();
      navigate(`/knowledge/documents/${result.id}`);
    } finally {
      setSaving(false);
    }
  };

  const cleanRebuild = async () => {
    const values = await rebuildForm.validateFields();
    setSaving(true);
    try {
      const result = await api.post<{ documents: number; jobs: number }>("/api/v1/admin/knowledge/rebuild/clean", {
        confirmation: values.confirmation,
        reason: values.reason,
      });
      message.success(`已清理知识索引并排队 ${result.jobs} 个重建任务`);
      setRebuildOpen(false);
      rebuildForm.resetFields();
      await refresh();
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <DataTable
        path="/api/v1/admin/knowledge/documents"
        filterKeys={DOC_FILTERS}
        extra={
          <Space>
            <Button danger onClick={() => setRebuildOpen(true)}>干净重建知识索引</Button>
            <Button type="primary" onClick={() => setCreateOpen(true)}>新增文档</Button>
          </Space>
        }
        toolbar={
          <Space wrap>
            <Input.Search
              allowClear
              style={{ width: 300 }}
              placeholder="文档标题"
              defaultValue={searchParams.get("search") ?? ""}
              onSearch={(v) => setParam("search", v)}
            />
            <Select
              allowClear
              style={{ width: 150 }}
              placeholder="状态"
              value={searchParams.get("status") ?? undefined}
              onChange={(v) => setParam("status", v)}
              options={["draft", "published", "archived"].map((v) => ({ value: v, label: v }))}
            />
          </Space>
        }
        columns={[
        {
          title: "标题",
          dataIndex: "title",
          render: (v: string, r: { id: string }) => <Link to={`/knowledge/documents/${r.id}`}>{v}</Link>,
        },
        { title: "知识库", dataIndex: "knowledgeBaseKey", width: 180 },
        { title: "可信级别", dataIndex: "trustLevel", width: 120, render: (v) => <StatusTag value={v} /> },
        { title: "来源", dataIndex: "sourceType", width: 140 },
        { title: "匹配关键词", dataIndex: "matchKeywords", width: 220, render: (v: string[]) => <Tags values={v} /> },
        {
          title: "角色设定",
          dataIndex: "isCharacterSetting",
          width: 100,
          render: (v: boolean) => (v ? "是" : "否"),
        },
        { title: "当前版本", dataIndex: "currentVersion", width: 100, render: (v) => (v ? `v${v}` : "—") },
        { title: "版本数", dataIndex: "versionCount", width: 80, align: "right" },
        { title: "切片", dataIndex: "chunkCount", width: 80, align: "right" },
        { title: "状态", dataIndex: "status", width: 100, render: (v) => <StatusTag value={v} /> },
        { title: "更新", dataIndex: "updatedAt", width: 170, render: fmtDateTime },
        ]}
      />

      <Modal
        title="新增知识文档"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={() => void createDocument()}
        confirmLoading={saving}
        width={760}
        destroyOnClose
      >
        <Form
          form={createForm}
          layout="vertical"
          preserve={false}
          initialValues={{ matchKeywords: [], targetChars: 500, overlapChars: 80, splitBy: "auto", isCharacterSetting: false }}
        >
          <Form.Item name="knowledgeBaseId" label="知识库" rules={[{ required: true }]}>
            <Select options={bases.filter((base) => base.status === "active").map((base) => ({ value: base.id, label: `${base.displayName}（${base.key}）` }))} />
          </Form.Item>
          <Form.Item name="title" label="标题" rules={[{ required: true }]}><Input maxLength={200} /></Form.Item>
          <Form.Item name="content" label="正文" rules={[{ required: true }]}><Input.TextArea rows={14} showCount maxLength={500_000} /></Form.Item>
          <Form.Item name="summary" label="摘要"><Input.TextArea rows={2} maxLength={2_000} /></Form.Item>
          <Form.Item
            name="matchKeywords"
            label="匹配关键词"
            extra="填写别名、昵称或专名；用户消息原样包含其中任一词时，会优先召回这篇文档。每个词至少 2 个字。"
          >
            <Select mode="tags" open={false} tokenSeparators={[",", "，", "、"]} maxTagCount="responsive" placeholder="输入后按回车，例如：司总、大王" />
          </Form.Item>
          <Form.Item name="isCharacterSetting" valuePropName="checked"><Checkbox>这是角色自身设定</Checkbox></Form.Item>
          <Space align="start">
            <Form.Item name="targetChars" label="目标切片字数"><InputNumber min={50} max={4_000} /></Form.Item>
            <Form.Item name="overlapChars" label="重叠字数"><InputNumber min={0} max={500} /></Form.Item>
            <Form.Item name="splitBy" label="切片策略"><Select style={{ width: 150 }} options={["auto", "paragraph", "song-line"].map((value) => ({ value, label: value }))} /></Form.Item>
          </Space>
          <Form.Item name="reason" label="操作理由" rules={[{ required: true }]}><Input maxLength={200} /></Form.Item>
        </Form>
      </Modal>

      <Modal
        title="开放用户前干净重建"
        open={rebuildOpen}
        onCancel={() => setRebuildOpen(false)}
        onOk={() => void cleanRebuild()}
        confirmLoading={saving}
        okButtonProps={{ danger: true }}
        destroyOnClose
      >
        <Alert type="warning" showIcon message="只会清理 knowledge_chunk 的切片和向量，不会删除用户记忆。重建完成并验证前请保持正式入口为维护状态。" />
        <Form form={rebuildForm} layout="vertical" preserve={false} style={{ marginTop: 16 }}>
          <Form.Item name="confirmation" label="输入 CLEAR_KNOWLEDGE_INDEX 确认" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="reason" label="操作理由" rules={[{ required: true }]}><Input maxLength={200} /></Form.Item>
        </Form>
      </Modal>
    </>
  );
}
